import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getEnquiries,
  markEnquiryRead,
  markEnquiryUnread,
  type Enquiry,
} from "@/lib/api/enquiries";
import { useToast } from "@/contexts/ToastContext";

const KEY = ["enquiries"] as const;

export function useEnquiries() {
  return useQuery({
    queryKey: KEY,
    queryFn: getEnquiries,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
}

export function useUnreadCount() {
  const { data: enquiries = [] } = useEnquiries();
  return enquiries.filter((e) => !e.read).length;
}

export function useMarkRead() {
  const qc = useQueryClient();
  const { error } = useToast();
  return useMutation({
    mutationFn: (id: string) => markEnquiryRead(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: KEY });
      const prev = qc.getQueryData<Enquiry[]>(KEY);
      qc.setQueryData<Enquiry[]>(KEY, (old) =>
        (old ?? []).map((e) => (e.id === id ? { ...e, read: true } : e)),
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData<Enquiry[]>(KEY, ctx.prev);
      error("Failed to mark as read.");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useMarkUnread() {
  const qc = useQueryClient();
  const { error } = useToast();
  return useMutation({
    mutationFn: (id: string) => markEnquiryUnread(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: KEY });
      const prev = qc.getQueryData<Enquiry[]>(KEY);
      qc.setQueryData<Enquiry[]>(KEY, (old) =>
        (old ?? []).map((e) => (e.id === id ? { ...e, read: false } : e)),
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) qc.setQueryData<Enquiry[]>(KEY, ctx.prev);
      error("Failed to mark as unread.");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}